import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["items", "next", "sentinel", "pagination"]

  connect() {
    this.loading = false

    if (this.hasPaginationTarget) {
      this.paginationTarget.classList.add("hidden")
    }

    if (!this.hasSentinelTarget) return
    if (!this.hasNextTarget || !this.nextTarget?.href) return

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.loadNext()
        }
      },
      {
        root: null,
        rootMargin: "700px 0px",
        threshold: 0.01
      }
    )

    this.observer.observe(this.sentinelTarget)
  }

  disconnect() {
    this.observer?.disconnect()
  }

  async loadNext() {
    if (this.loading) return
    if (!this.hasItemsTarget) return
    if (!this.hasNextTarget || !this.nextTarget?.href) return

    this.loading = true

    const nextUrl = this.nextTarget.href
    const sentinel = this.hasSentinelTarget ? this.sentinelTarget : null
    const previousHTML = sentinel?.innerHTML || ""

    if (sentinel) {
      sentinel.classList.add("is-loading")
      sentinel.innerHTML = this.loadingHTML()
    }

    try {
      const response = await fetch(nextUrl, {
        method: "GET",
        credentials: "same-origin",
        headers: { Accept: "text/html" }
      })

      if (!response.ok) {
        throw new Error(`Failed to load next page (${response.status})`)
      }

      const html = await response.text()
      const doc = new DOMParser().parseFromString(html, "text/html")
      const newItems = doc.querySelector('[data-infinite-scroll-target="items"]')

      if (!newItems) {
        this.stop()
        return
      }

      const fragment = document.createDocumentFragment()
      Array.from(newItems.children).forEach((child) => fragment.appendChild(child))
      this.itemsTarget.appendChild(fragment)

      const newNext = doc.querySelector('[data-infinite-scroll-target="next"]')
      const newHref = newNext?.getAttribute("href")

      if (newHref) {
        this.nextTarget.setAttribute("href", newHref)
      } else {
        this.stop()
      }
    } catch (error) {
      console.error(error)

      if (this.hasPaginationTarget) {
        this.paginationTarget.classList.remove("hidden")
      }

      this.stop()
    } finally {
      if (sentinel) {
        sentinel.classList.remove("is-loading")
        sentinel.innerHTML = previousHTML
      }

      this.loading = false
    }
  }

  loadingHTML() {
    return '<div class="h-px w-full"></div>'
  }

  stop() {
    const sentinel = this.hasSentinelTarget ? this.sentinelTarget : null
    const nextLink = this.hasNextTarget ? this.nextTarget : null

    if (this.observer && sentinel) {
      this.observer.unobserve(sentinel)
    }

    this.observer?.disconnect()
    sentinel?.remove()
    nextLink?.removeAttribute("href")
  }
}
